import React, { forwardRef, useImperativeHandle } from "react";
import { Clock } from "lucide-react";

import {
    Button,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormHelperText,
    Typography,

} from "@mui/material";
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import './index.scss'
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from "dayjs";
import { Textarea } from "uiw";
import moment from "moment";


const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const positions = ['first', 'second', 'third', 'fourth', 'last'];
const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];


const generateYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => currentYear + i);
};

export interface RecurringTaskFormRef {
    submitForm: () => Promise<any>;
}

const RecurringTaskForm = ({ values, errors, touched, setFieldValue, setFieldTouched }: any) => {

    const handleDayToggle = (day: typeof days[number]) => {

        const updatedDays = values.weeklyDays.includes(day)
            ? values.weeklyDays.filter((d: any) => d !== day)
            : [...values.weeklyDays, day];
        setFieldValue('recurringForm.weeklyDays', updatedDays);
    };
    const getLastDayOfMonth = (month: any, year: any) => {
        const monthNames: any = {
            january: 0,
            february: 1,
            march: 2,
            april: 3,
            may: 4,
            june: 5,
            july: 6,
            august: 7,
            september: 8,
            october: 9,
            november: 10,
            december: 11,
        };
        const monthIndex = monthNames[month.toLowerCase()];
        if (monthIndex !== undefined) {
            return moment({ year, month: monthIndex })
                .endOf("month")
                .format("YYYY-MM-DD");
        }
    }

    const getDayNumber = (day: any) => {
        const days: any = {
            sunday: 0,
            monday: 1,
            tuesday: 2,
            wednesday: 3,
            thursday: 4,
            friday: 5,
            saturday: 6,
        };
        return days[day];
    }

    const getScheduledDates = (dayOfWeek: any, repeatEvery: any) => {
        const dates = [];
        const currentDate = new Date();
        const targetDay = getDayNumber(dayOfWeek);
        const currentDay = currentDate.getDay();
        let diff = targetDay - currentDay;
        if (diff <= 0) {
            diff += 7;
        }
        currentDate.setDate(currentDate.getDate() + diff);

        for (let i = 0; i < repeatEvery; i++) {
            dates.push(moment(currentDate).format("YYYY-MM-DD"));
            currentDate.setDate(currentDate.getDate() + 7);
        }
        return dates;
    };


    const selectedOccurrence = (values: any) => {
        let occurrences = "";
        if (values.frequency === 'daily') {
            if (values.endDateType === "date" && values.endDate) {
                const startDate = moment(values.startDate);
                const endDate = moment(values.endDate);
                let currentDate = startDate.clone();
                const dates = []
                while (currentDate < endDate) {
                    dates.push(currentDate.format("YYYY-MM-DD"));
                    currentDate.add(1, "days");
                }
                occurrences = `No of Occurrences: ${dates.length}\nDates: ${dates.join(", ")}`;
            }

            if (values.endDateType === "occurrences" && values.endAfterOccurrences) {
                const startDate = moment(values.startDate);
                const endAfterOccurrences = (values.endAfterOccurrences);
                const dates = []
                for (let i = 0; i < endAfterOccurrences; i++) {
                    startDate.add(1, "days");
                    dates.push(startDate.format("YYYY-MM-DD"));
                }
                occurrences = `No of Occurrences: ${dates.length}\nDates: ${dates.join(", ")}`;
            }

            if (values.endDateType === "monthYear" && values.endMonth) {
                const startDate = moment(values.startDate);
                const endMonth = values.endMonth;
                const endYear = values.endYear;
                const lastDay = moment(
                    getLastDayOfMonth(endMonth.toLowerCase(), endYear)
                );
                let currentDate = startDate.clone();
                const dates = [];
                while (currentDate <= lastDay) {
                    dates.push(currentDate.format("YYYY-MM-DD"));
                    currentDate.add(1, "days");
                }
                occurrences = `No of Occurrences: ${dates.length}\nDates: ${dates.join(", ")}`;
            }
        }
        if (values.frequency === 'weekly' && values.weeklyDays.length > 0) {
            const weeklyDays = values.weeklyDays;
            const repeatEvery = values?.repeatEvery;
            let dates: string[] = [];

            for (let i = 0; i < weeklyDays.length; i++) {
                const day = weeklyDays[i];
                const dayDates = getScheduledDates(day.toLowerCase(), repeatEvery);
                dates = dates.concat(dayDates);
            }

            occurrences = `No of Occurrences: ${dates.length}\nDates: ${dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime()).join(", ")}`;
        }
        if (values.frequency === 'yearly') {
            const startDate = moment(values.startDate);
            const endAfterOccurrences = values.endAfterOccurrences;
            const dates = []
            for (let i = 0; i < endAfterOccurrences; i++) {
                dates.push(
                    startDate.clone().add(i, "years").format("YYYY-MM-DD")
                );
                occurrences = `No of Occurrences: ${dates.length}\nDates: ${dates.join(", ")}`;
            }
        }

        if (values.frequency === 'monthly') {
            const monthlyType = values.monthlyType;
            const monthlyDay = values.monthlyDay;
            const endMonth = values.endMonth;
            const endYear = values.endYear;
            const lastDay = moment(
                getLastDayOfMonth(endMonth.toLowerCase(), endYear)
            );

            if (monthlyType === "day" && monthlyDay && endMonth && endYear) {
                const startDate = moment(values.startDate);
                const selectDay = parseInt(monthlyDay);
                startDate.startOf("day");
                lastDay.startOf("day");
                let currentDate = startDate.clone();
                const dates = [];
                while (currentDate <= lastDay) {
                    const selectedDate = currentDate.clone().date(selectDay);
                    if (
                        selectedDate.isValid() &&
                        selectedDate >= startDate &&
                        selectedDate <= lastDay
                    ) {
                        dates.push(selectedDate.format("YYYY-MM-DD"));
                    }
                    currentDate.add(1, "month");
                }
                occurrences = `No of Occurrences: ${dates.length}\nDates: ${dates.join(", ")}`;
            }

            if (monthlyType === "position" && values.monthlyPosition && values.monthlySelectedDay && endMonth && endYear) {
                const monthlyPosition = values.monthlyPosition;
                const monthlySelectedDay = values.monthlySelectedDay;

                const dayPosition = monthlyPosition.toLowerCase();
                const weekday = monthlySelectedDay.toLowerCase();

                const daysOfWeek = [
                    "sunday",
                    "monday",
                    "tuesday",
                    "wednesday",
                    "thursday",
                    "friday",
                    "saturday",
                ];
                const weekdayIndex = daysOfWeek.indexOf(weekday);
                const startDate = moment().startOf("day");

                let currentDate = moment(startDate);
                const dates = [];

                while (currentDate <= lastDay) {
                    const firstDayOfMonth = currentDate.clone().startOf("month");

                    let firstSelectedDay = firstDayOfMonth.clone().day(weekdayIndex);

                    if (firstSelectedDay.isBefore(firstDayOfMonth)) {
                        firstSelectedDay.add(1, "week");
                    }

                    let nthSelectedDay: moment.Moment | undefined;
                    if (dayPosition === "first") {
                        nthSelectedDay = firstSelectedDay;
                    } else if (dayPosition === "second") {
                        nthSelectedDay = firstSelectedDay.clone().add(1, "week");
                    } else if (dayPosition === "third") {
                        nthSelectedDay = firstSelectedDay.clone().add(2, "weeks");
                    } else if (dayPosition === "fourth") {
                        nthSelectedDay = firstSelectedDay.clone().add(3, "weeks");
                    } else if (dayPosition === "last") {
                        nthSelectedDay = firstSelectedDay.clone().add(3, "weeks");

                        while (nthSelectedDay.month() === currentDate.month()) {
                            nthSelectedDay.add(1, "week");
                        }
                        nthSelectedDay.subtract(1, "week");
                    }

                    if (nthSelectedDay &&
                        nthSelectedDay.isValid() &&
                        nthSelectedDay.isBetween(startDate, lastDay, null, "[]")
                    ) {
                        dates.push(nthSelectedDay.format("YYYY-MM-DD"));
                    }

                    currentDate.add(1, "month");
                }
                occurrences = `No of Occurrences: ${dates.length}\nDates: ${dates.join(", ")}`;
            }
        }

        return occurrences;
    }

    return (
        <form
            className="form">
            <div className="recurring-title">
                <Clock className="w-5 h-5" />
                <Typography variant="h6" gutterBottom>Recurring Task Form</Typography>
            </div>

            {/* Frequency Selection */}
            <div className="space-y-5 how-often">
                <div>
                    <InputLabel>How often?*</InputLabel>
                    <FormControl

                        error={Boolean(errors.frequency && touched.frequency)}
                    >
                        <Select
                            className="select-frequency"
                            name="frequency"
                            value={values.frequency}
                            onChange={(e) => setFieldValue("recurringForm.frequency", e.target.value)}
                            onBlur={() => setFieldTouched("recurringForm.frequency", true)}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>
                                Select frequency
                            </MenuItem>
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                            {/* <MenuItem value="custom">Custom</MenuItem> */}
                        </Select>
                        <FormHelperText>
                            {errors.frequency && touched.frequency ? errors.frequency : ""}
                        </FormHelperText>
                    </FormControl>
                </div>
                <div>
                    <InputLabel>Selected occurrences</InputLabel>
                    <Textarea
                        value={selectedOccurrence(values)}
                        readOnly
                        className="selected-occurrences"
                    ></Textarea>
                </div>
            </div>

            {/* Yearly options */}
            {values.frequency === "yearly" && (
                <div>
                    <InputLabel>Number of occurrences*</InputLabel>
                    <TextField
                        name="endAfterOccurrences"
                        type="number"
                        value={values.endAfterOccurrences}
                        onChange={(e) => setFieldValue("recurringForm.endAfterOccurrences", e.target.value)}
                        onBlur={() => setFieldTouched("recurringForm.endAfterOccurrences", true)}
                        error={touched.endAfterOccurrences && Boolean(errors.endAfterOccurrences)}
                        helperText={touched.endAfterOccurrences && errors.endAfterOccurrences}
                        placeholder="Number of occurrences"
                        fullWidth
                    />
                </div>
            )}

            {/* Weekly Options */}
            {values.frequency === "weekly" && (
                <div className="space-y-4">
                    <InputLabel>Repeat every</InputLabel>
                    <FormControl>
                        <TextField
                            type="number"
                            name="repeatEvery"
                            value={values.repeatEvery}
                            onChange={(e) => setFieldValue("recurringForm.repeatEvery", e.target.value)}
                            onBlur={() => setFieldTouched("recurringForm.repeatEvery", true)}
                            error={touched.repeatEvery && Boolean(errors.repeatEvery)}
                            fullWidth
                        />
                        <div className="day-group" onBlur={() => setFieldTouched("recurringForm.weeklyDays", true)}>
                            {days.map((day: string) => (
                                <Button
                                    key={day}
                                    variant={values.weeklyDays.includes(day) ? 'contained' : 'outlined'}
                                    onClick={() => handleDayToggle(day)}
                                    className="w-24"
                                    name="weeklyDays"
                                >
                                    {day}
                                </Button>
                            ))}
                        </div>
                        <FormHelperText error={touched.weeklyDays && Boolean(errors.weeklyDays)}>
                            {errors.weeklyDays && touched.weeklyDays
                                ? errors.weeklyDays
                                : ""}
                        </FormHelperText>
                    </FormControl>
                </div>
            )}

            {/* Monthly Options */}

            {values.frequency === "monthly" && (
                <div className="monthly-option">
                    <InputLabel>Monthly pattern*</InputLabel>
                    <FormControl fullWidth
                        error={touched.monthlyType && Boolean(errors.monthlyType)}
                        className="monthly-content">

                        <Select
                            fullWidth
                            name="monthlyType"
                            value={values.monthlyType}
                            onChange={(e) => setFieldValue("recurringForm.monthlyType", e.target.value)}
                            onBlur={() => setFieldTouched("recurringForm.monthlyType", true)}
                            displayEmpty
                            error={touched.monthlyType && Boolean(errors.monthlyType)}
                        >
                            <MenuItem value="" disabled>
                                Select pattern type
                            </MenuItem>
                            <MenuItem value="day">On day of month</MenuItem>
                            <MenuItem value="position">On specific position</MenuItem>
                        </Select>
                        <FormHelperText>
                            {errors.monthlyType && touched.monthlyType ? errors.monthlyType : ""}
                        </FormHelperText>
                    </FormControl>


                    {values.monthlyType === "day"
                        && (
                            <TextField
                                type="number"
                                name="monthlyDay"
                                value={values.monthlyDay}
                                onChange={(e) => setFieldValue("recurringForm.monthlyDay", e.target.value)}
                                onBlur={() => setFieldTouched("recurringForm.monthlyDay", true)}
                                error={touched.monthlyDay && Boolean(errors.monthlyDay)}
                                helperText={touched.monthlyDay && errors.monthlyDay}
                                fullWidth
                                placeholder="Enter day (1-31)*"
                            />
                        )}

                    {values.monthlyType === "position"
                        && (
                            <div className="postion">
                                <FormControl fullWidth error={touched.monthlyPosition && Boolean(errors.monthlyPosition)}>
                                    <Select
                                        name="monthlyPosition"

                                        value={values.monthlyPosition}
                                        onChange={(e) => setFieldValue("recurringForm.monthlyPosition", e.target.value)}

                                        onBlur={() => setFieldTouched("recurringForm.monthlyPosition", true)}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select position*
                                        </MenuItem>
                                        {positions.map((position) => (
                                            <MenuItem key={position} value={position}>
                                                {position}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {errors.monthlyPosition && touched.monthlyPosition ? errors.monthlyPosition : ""}
                                    </FormHelperText>
                                </FormControl>
                                <FormControl fullWidth error={touched.monthlySelectedDay && Boolean(errors.monthlySelectedDay)}>
                                    <Select
                                        name="monthlySelectedDay"

                                        value={values.monthlySelectedDay}

                                        onChange={(e) => setFieldValue("recurringForm.monthlySelectedDay", e.target.value)}

                                        onBlur={() => setFieldTouched("recurringForm.monthlySelectedDay", true)}
                                        error={touched.monthlySelectedDay && Boolean(errors.monthlySelectedDay)}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select day*
                                        </MenuItem>
                                        {days.map((day) => (
                                            <MenuItem key={day} value={day}>
                                                {day}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>
                                        {errors.monthlySelectedDay && touched.monthlySelectedDay ? errors.monthlySelectedDay : ""}
                                    </FormHelperText>
                                </FormControl>
                            </div>
                        )}
                </div>
            )}

            <div className="space-y-2 element-2">
                <InputLabel>Start date*</InputLabel>
                <FormControl
                    fullWidth
                    error={Boolean(errors.startDate && touched.startDate)}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={['DatePicker']}>
                            <DatePicker
                                value={values.startDate ? dayjs(values.startDate) : null}
                                onChange={(value) => setFieldValue("recurringForm.startDate", value, true)}
                                name="startDate"
                            />
                        </DemoContainer>
                    </LocalizationProvider>
                    <FormHelperText>
                        {errors.startDate && touched.startDate ? String(errors.startDate) : ""}
                    </FormHelperText>
                </FormControl>
            </div>


            {(values.frequency === "daily") && (
                <div className="End-date">
                    <InputLabel>End date options</InputLabel>
                    <FormControl
                        fullWidth
                        error={Boolean(errors.endDateType && touched.endDateType)}
                    >
                        <Select
                            name="endDateType"
                            value={values.endDateType}
                            onChange={(e) => setFieldValue("recurringForm.endDateType", e.target.value)}
                            onBlur={() => setFieldTouched("recurringForm.endDateType", true)}
                            displayEmpty
                        >
                            <MenuItem value="" disabled>
                                Select end condition
                            </MenuItem>
                            {/* <MenuItem value="never">never</MenuItem> */}
                            <MenuItem value="date">date</MenuItem>
                            <MenuItem value="occurrences">occurrences</MenuItem>
                            <MenuItem value="monthYear">monthYear</MenuItem>
                        </Select>
                        <FormHelperText>
                            {errors.endDateType && touched.endDateType ? errors.endDateType : ""}

                        </FormHelperText>
                    </FormControl>
                </div>
            )}

            {/* date */}
            {(values.endDateType === "date") && (values.frequency === "daily")
                && (
                    <div>
                        <InputLabel>End date*</InputLabel>
                        <FormControl
                            fullWidth
                        >
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DatePicker']}>
                                    <DatePicker label="Select end date"
                                        value={values.endDate ? dayjs(values.endDate) : null}

                                        onChange={(value) => {
                                            const selectedValue = value ? value.toISOString() : null;
                                            setFieldValue('recurringForm.endDate', selectedValue, true);
                                            if (selectedValue) {
                                                setFieldTouched('recurringForm.endDate', true);
                                            }
                                        }}
                                        onClose={() => setFieldTouched("recurringForm.endDate", true)}

                                        name="endDate"
                                        slotProps={{
                                            textField: {
                                                error: Boolean(errors.endDate && touched.endDate),
                                                helperText: errors.endDate && touched.endDate ? String(errors.endDate) : "",
                                            },
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </FormControl>
                    </div>
                )}

            {values.endDateType === "occurrences" && (values.frequency === "daily")
                && (

                    <div>
                        <InputLabel>Number of occurrences*</InputLabel>
                        <TextField
                            name="endAfterOccurrences"
                            type="number"
                            value={values.endAfterOccurrences}
                            onChange={(e) => setFieldValue("recurringForm.endAfterOccurrences", e.target.value)}
                            onBlur={() => setFieldTouched("recurringForm.endAfterOccurrences", true)}
                            error={touched.endAfterOccurrences && Boolean(errors.endAfterOccurrences)}
                            helperText={touched.endAfterOccurrences && errors.endAfterOccurrences}
                            placeholder="Number of occurrences"
                            fullWidth
                        />
                    </div>
                )}

            {(values.frequency === "daily" && values.endDateType === "monthYear") || values.frequency === "monthly" ? (
                <div className="month-year">
                    {/* Month Select */}
                    <FormControl fullWidth
                        error={Boolean(errors.endMonth && touched.endMonth)}
                    >
                        <InputLabel className='bg-white' id="month-select-label">End month*</InputLabel>
                        <Select
                            name="endMonth"
                            labelId="month-select-label"
                            value={values.endMonth}
                            onChange={(e) => setFieldValue("recurringForm.endMonth", e.target.value)}
                            onBlur={() => setFieldTouched("recurringForm.endMonth", true)}
                        >
                            {months.map((month) => (
                                <MenuItem key={month} value={month.toLowerCase()}>
                                    {month}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {errors.endMonth && touched.endMonth ? errors.endMonth : ""}
                        </FormHelperText>
                    </FormControl>

                    {/* Year Select */}
                    <FormControl fullWidth
                        error={Boolean(errors.endYear && touched.endYear)}>
                        <InputLabel className='bg-white' id="year-select-label">End year*</InputLabel>
                        <Select
                            labelId="year-select-label"
                            value={values.endYear}
                            onChange={(e) => setFieldValue("recurringForm.endYear", e.target.value)}
                            onBlur={() => setFieldTouched("recurringForm.endYear", true)}
                            name="endYear"
                        >
                            {generateYears().map((year) => (
                                <MenuItem key={year} value={year.toString()}>
                                    {year}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>
                            {errors.endYear && touched.endYear ? errors.endYear : ""}
                        </FormHelperText>
                    </FormControl>
                </div>
            ) : null}



            <div className="space-y-4 element-4">
                <  InputLabel>Priority</InputLabel>
                <FormControl
                    fullWidth

                    error={Boolean(errors.priority && touched.priority)}
                >
                    <Select
                        labelId="priority-select-label"
                        value={values.priority}
                        onChange={(e) => setFieldValue("recurringForm.priority", e.target.value)}
                        name="priority"
                        displayEmpty
                    >
                        <MenuItem value="" disabled>
                            Select priority
                        </MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                    </Select>
                    <FormHelperText>

                        {errors.priority && touched.priority ? errors.priority : ""}
                    </FormHelperText>
                </FormControl>
            </div>

            {/* Notes */}
            <div className="space-y-2 element-5">
                <InputLabel >Notes</InputLabel>
                <FormControl fullWidth>
                    <TextField

                        value={values.notes}
                        onChange={(e) => setFieldValue("recurringForm.notes", e.target.value)}

                        name="notes"
                        placeholder="Add any additional notes..."
                        multiline
                        rows={4}
                        fullWidth
                        variant="outlined"
                    />
                </FormControl>
            </div>
        </form>
    );
};

export default RecurringTaskForm;
